function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`DOM element #${id} not found`);
  }
  return el as T;
}

function svgById<T extends SVGElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`SVG element #${id} not found`);
  }
  return el as unknown as T;
}

export const dom = {
  canvas: byId<HTMLCanvasElement>("viewer"),

  statusElement: byId<HTMLParagraphElement>("status"),
  infoElement: byId<HTMLPreElement>("model-info"),
  selectionInfoElement: byId<HTMLPreElement>("selection-info"),

  fileInput: byId<HTMLInputElement>("ifc-input"),
  fitButton: byId<HTMLButtonElement>("fit-camera"),
  wireframeButton: byId<HTMLButtonElement>("toggle-wireframe"),
  clearButton: byId<HTMLButtonElement>("clear-scene"),
  multiselectToggleButton: byId<HTMLButtonElement>("multiselect-toggle"),

  hideSelectedButton: byId<HTMLButtonElement>("hide-selected"),
  isolateSelectedButton: byId<HTMLButtonElement>("isolate-selected"),
  showAllHiddenButton: byId<HTMLButtonElement>("show-all-hidden"),
  screenshotButton: byId<HTMLButtonElement>("screenshot-btn"),

  toggleMeasureButton: byId<HTMLButtonElement>("toggle-measure"),
  clearMeasureHistoryButton: byId<HTMLButtonElement>("clear-measure-history"),
  measureInfoElement: byId<HTMLPreElement>("measure-info"),
  measureHistoryElement: byId<HTMLOListElement>("measure-history"),
  measureUnitSelect: byId<HTMLSelectElement>("measure-unit"),
  measureSnapSelect: byId<HTMLSelectElement>("measure-snap"),

  filterStoreySelect: byId<HTMLSelectElement>("filter-storey"),
  filterTypeSelect: byId<HTMLSelectElement>("filter-type"),
  filterMarkInput: byId<HTMLInputElement>("filter-mark"),
  applyFiltersButton: byId<HTMLButtonElement>("apply-filters"),
  clearFiltersButton: byId<HTMLButtonElement>("clear-filters"),

  compareIfcInput: byId<HTMLInputElement>("compare-ifc-input"),
  compareCurrentFileEl: byId<HTMLSpanElement>("compare-current-file"),
  compareBaselineFileEl: byId<HTMLSpanElement>("compare-baseline-file"),
  compareAddFromPcButton: byId<HTMLButtonElement>("compare-add-from-pc"),
  compareCdeBlock: byId<HTMLDivElement>("compare-cde-block"),
  compareCdeRefreshButton: byId<HTMLButtonElement>("compare-cde-refresh"),
  compareCdeOpenButton: byId<HTMLButtonElement>("compare-cde-open"),
  compareCdeListSelect: byId<HTMLSelectElement>("compare-cde-list"),
  runCompareButton: byId<HTMLButtonElement>("run-compare"),
  clearCompareButton: byId<HTMLButtonElement>("clear-compare"),
  compareShowRemovedToggle: byId<HTMLInputElement>("compare-show-removed"),
  compareStatsElement: byId<HTMLPreElement>("compare-stats"),

  ifcAddMenu: byId<HTMLDivElement>("ifc-add-menu"),
  ifcAddFromPcButton: byId<HTMLButtonElement>("ifc-add-from-pc"),
  ifcPresentationList: byId<HTMLDivElement>("ifc-presentation-list"),

  // ---- Settings: language / theme / PWA ----
  settingsLangSelect: byId<HTMLSelectElement>("settings-lang-select"),
  settingsThemeSelect: byId<HTMLSelectElement>("settings-theme-select"),
  settingsPwaInstallButton: byId<HTMLButtonElement>("settings-pwa-install"),
  settingsPwaHint: byId<HTMLParagraphElement>("settings-pwa-hint"),
  settingsGridToggle: byId<HTMLInputElement>("settings-grid-toggle"),

  sideRail: byId<HTMLElement>("side-rail"),
  sideRailToggle: byId<HTMLButtonElement>("side-rail-toggle"),
  sideRailBackdrop: byId<HTMLDivElement>("side-rail-backdrop"),

  addAnnotationBtn: byId<HTMLButtonElement>("add-annotation-btn"),
  openAnnotationsListBtn: byId<HTMLButtonElement>("open-annotations-list-btn"),
  annotationFormOverlay: byId<HTMLDivElement>("annotation-form-overlay"),
  annotationFormTitle: byId<HTMLDivElement>("annotation-form-title"),
  annotationTitleInput: byId<HTMLInputElement>("annotation-title"),
  annotationPrioritySelect: byId<HTMLSelectElement>("annotation-priority"),
  annotationDeadlineInput: byId<HTMLInputElement>("annotation-deadline"),
  annotationDescriptionEl: byId<HTMLDivElement>("annotation-description"),
  annotationFormCancelBtn: byId<HTMLButtonElement>("annotation-form-cancel"),
  annotationFormSaveBtn: byId<HTMLButtonElement>("annotation-form-save"),
  annotationListOverlay: byId<HTMLDivElement>("annotation-list-overlay"),
  annotationListRoot: byId<HTMLDivElement>("annotation-list-root"),
  annotationExportWordBtn: byId<HTMLButtonElement>("annotation-export-word"),
  annotationExportBcfBtn: byId<HTMLButtonElement>("annotation-export-bcf"),
  annotationImportBcfBtn: byId<HTMLButtonElement>("annotation-import-bcf-btn"),
  annotationImportBcfInput: byId<HTMLInputElement>("annotation-import-bcf-input"),
  annotationListCloseBtn: byId<HTMLButtonElement>("annotation-list-close"),
  shareLinkBtn: byId<HTMLButtonElement>("share-link-btn"),
  shareLinkUrlInput: byId<HTMLInputElement>("share-link-url"),
  shareLinkCopyBtn: byId<HTMLButtonElement>("share-link-copy-btn"),
  annotationImageMenu: byId<HTMLDivElement>("annotation-image-menu"),

  ifcTabsElement: byId<HTMLDivElement>("ifc-tabs"),

  treeRoot: byId<HTMLDivElement>("ifc-tree-root"),
  treeExpandAllButton: byId<HTMLButtonElement>("tree-expand-all"),
  treeCollapseAllButton: byId<HTMLButtonElement>("tree-collapse-all"),

  propertiesRoot: byId<HTMLDivElement>("properties-root"),

  clipEnabledCheckbox: byId<HTMLInputElement>("clip-enabled"),
  clipGrid: byId<HTMLDivElement>("clip-grid"),
  clipResetButton: byId<HTMLButtonElement>("clip-reset"),
  clipPlanLock: byId<HTMLParagraphElement>("clip-plan-lock"),

  viewNameInput: byId<HTMLInputElement>("view-name-input"),
  viewSaveButton: byId<HTMLButtonElement>("view-save-btn"),
  viewsListElement: byId<HTMLDivElement>("views-list"),

  searchInput: byId<HTMLInputElement>("search-input"),
  searchRunButton: byId<HTMLButtonElement>("search-run-btn"),
  searchStatusElement: byId<HTMLParagraphElement>("search-status"),
  searchResultsElement: byId<HTMLDivElement>("search-results"),

  colorRuleModeSelect: byId<HTMLSelectElement>("color-rule-mode"),
  colorRuleApplyButton: byId<HTMLButtonElement>("color-rule-apply"),
  colorRuleClearButton: byId<HTMLButtonElement>("color-rule-clear"),
  colorRuleLegendElement: byId<HTMLDivElement>("color-rule-legend"),

  planOpenButton: byId<HTMLButtonElement>("plan-open-btn"),
  planStoreyList: byId<HTMLDivElement>("plan-storey-list"),
  planStatus: byId<HTMLParagraphElement>("plan-status"),

  dropZoneOverlay: byId<HTMLDivElement>("drop-zone-overlay"),

  progressOverlay: byId<HTMLDivElement>("progress-overlay"),
  progressLabel: byId<HTMLDivElement>("progress-overlay-label"),
  progressRingFill: svgById<SVGCircleElement>("progress-ring-fill"),
  progressRingPct: byId<HTMLSpanElement>("progress-ring-pct"),
};

export const mqCompactUi = window.matchMedia("(max-width: 768px)");

export function isCompactUi(): boolean {
  return mqCompactUi.matches;
}
